/**
 * RFC-180 M4 G8.3 — Reviews → Access Reviews → Roles catalog.
 *
 * Program-level role definitions. Each role declares the expected raw
 * role names per app; the AR reconciler's Rule 6 (role_mismatch) flags
 * employees whose actual access on an app doesn't match any expectation.
 *
 * Surface mirrors the Custom Apps catalog — workspace-wide registry that
 * cycles inherit automatically.
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { RoleDefinitionDTO } from '@deepguide-ai/types';
import { trpc } from '@/lib/trpc';
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions';
import { Button } from '@deepguide-ai/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog';
import { Input } from '@deepguide-ai/ui/input';
import { Label } from '@deepguide-ai/ui/label';
import { Textarea } from '@deepguide-ai/ui/textarea';

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/reviews/access_/roles',
)({
  component: RolesCatalogPage,
  staticData: { pageHeader: { title: 'Roles' } },
});

function RolesCatalogPage() {
  const { workspaceId } = useParams({ strict: false }) as { workspaceId: string };
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId);
  const rolesQuery = useQuery(trpc.roleDefinition.list.queryOptions());
  const [editingRole, setEditingRole] = useState<RoleDefinitionDTO | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  return (
    <div className='font-body px-6 py-6'>
      <header className='mb-6 flex items-center justify-between'>
        <div>
          <h1 className='text-[22px] font-semibold text-text-secondary'>Roles</h1>
          <p className='mt-1 text-[12px] text-text-subtle'>
            Define expected access per app for each org role. The AR reconciler
            flags employees whose access doesn't match. Custom applications
            are registered from an open review period's Applications stage.
          </p>
        </div>
        {canWorkspaceWrite && (
          <Button size='sm' className='h-9' onClick={() => setIsCreating(true)}>
            <Plus className='mr-1 h-4 w-4' />
            New role
          </Button>
        )}
      </header>

      {rolesQuery.isLoading ? (
        <div className='py-12 text-center text-[12px] text-text-subtle'>Loading…</div>
      ) : (rolesQuery.data ?? []).length === 0 ? (
        <div className='rounded-sm border border-dashed border-border py-12 text-center text-[12px] text-text-subtle'>
          No roles yet. Define one to enable role_mismatch detection in AR cycles.
        </div>
      ) : (
        <div className='overflow-hidden rounded-sm border border-border'>
          <table className='w-full text-[12px]'>
            <thead className='bg-surface-subtle'>
              <tr className='h-[40px] text-left text-[10px] uppercase tracking-wide text-text-subtle'>
                <th className='px-4'>Name</th>
                <th className='px-4'>Description</th>
                <th className='px-4'>Expected per app</th>
                <th className='px-4'>Employees</th>
                <th className='px-4'>Added</th>
                {canWorkspaceWrite && <th className='px-4 text-right'>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {((rolesQuery.data ?? []) as Array<RoleDefinitionDTO & { _count?: { employees: number } }>).map((role) => {
                const expected = role.expectedRolesByApp ?? {};
                const apps = Object.keys(expected);
                return (
                  <tr
                    key={role.id}
                    className='h-[44px] border-t border-border text-text-secondary'
                  >
                    <td className='px-4 font-medium'>{role.name}</td>
                    <td className='px-4 text-text-body'>{role.description ?? '—'}</td>
                    <td className='px-4 text-text-body'>
                      {apps.length === 0 ? (
                        '—'
                      ) : (
                        <span className='font-mono text-[10px] text-text-subtle'>
                          {apps
                            .map(
                              (a) =>
                                `${a}: [${(expected[a] ?? []).join(', ') || '*'}]`,
                            )
                            .join('  ·  ')}
                        </span>
                      )}
                    </td>
                    <td className='px-4 text-text-body'>
                      {role._count?.employees ?? 0}
                    </td>
                    <td className='px-4 text-text-subtle'>
                      {format(new Date(role.createdAt), 'MMM d, yyyy')}
                    </td>
                    {canWorkspaceWrite && (
                      <td className='px-4 text-right'>
                        <div className='flex justify-end gap-1'>
                          <Button
                            size='sm'
                            variant='ghost'
                            className='h-7 px-2'
                            onClick={() => setEditingRole(role)}
                          >
                            <Pencil className='h-3.5 w-3.5' />
                          </Button>
                          <DeleteRoleButton
                            roleId={role.id}
                            roleName={role.name}
                            readOnly={!canWorkspaceWrite}
                          />
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(isCreating || editingRole) && (
        <RoleDefinitionDialog
          open
          onOpenChange={(o) => {
            if (!o) {
              setIsCreating(false);
              setEditingRole(null);
            }
          }}
          existing={editingRole ?? undefined}
          readOnly={!canWorkspaceWrite}
        />
      )}
    </div>
  );
}

function DeleteRoleButton({
  roleId,
  roleName,
  readOnly = false,
}: {
  roleId: string;
  roleName: string;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.roleDefinition.delete.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trpc.roleDefinition.list.queryKey() });
      toast.success(`${roleName} deleted`);
    },
    onError: (err: Error) => toast.error(err.message ?? 'Delete failed'),
  });
  return (
    <Button
      size='sm'
      variant='ghost'
      className='h-7 px-2 text-status-red hover:bg-status-red-bg'
      disabled={mutation.isPending}
      onClick={() => {
        if (readOnly) return;
        if (confirm(`Delete role "${roleName}"? Employees with this role lose the assignment.`)) {
          mutation.mutate({ id: roleId });
        }
      }}
    >
      <Trash2 className='h-3.5 w-3.5' />
    </Button>
  );
}

function RoleDefinitionDialog({
  open,
  onOpenChange,
  existing,
  readOnly = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: RoleDefinitionDTO;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [expectedText, setExpectedText] = useState(
    JSON.stringify(existing?.expectedRolesByApp ?? { azure: ['User'], github: ['write'] }, null, 2),
  );

  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions({ framework: 'SOC2' }),
  );
  const programId = (programQuery.data as { id?: string } | null | undefined)?.id;

  const createMutation = useMutation({
    ...trpc.roleDefinition.create.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trpc.roleDefinition.list.queryKey() });
      toast.success('Role created');
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(err.message ?? 'Create failed'),
  });

  const updateMutation = useMutation({
    ...trpc.roleDefinition.update.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trpc.roleDefinition.list.queryKey() });
      toast.success('Role updated');
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(err.message ?? 'Update failed'),
  });

  const onSubmit = () => {
    if (readOnly) return;
    let parsedExpected: Record<string, string[]>;
    try {
      parsedExpected = JSON.parse(expectedText);
    } catch {
      toast.error('Expected-roles JSON is invalid');
      return;
    }
    if (typeof parsedExpected !== 'object' || Array.isArray(parsedExpected)) {
      toast.error('Expected roles must be a {app: [role]} object');
      return;
    }

    if (existing) {
      updateMutation.mutate({
        id: existing.id,
        name: name.trim(),
        description: description.trim() || null,
        expectedRolesByApp: parsedExpected,
      });
    } else {
      if (!programId) {
        toast.error('No compliance program found');
        return;
      }
      createMutation.mutate({
        programId,
        name: name.trim(),
        description: description.trim() || undefined,
        expectedRolesByApp: parsedExpected,
      });
    }
  };

  const pending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[560px]'>
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit role' : 'New role'}</DialogTitle>
          <DialogDescription>
            Role + per-app expectations. The reconciler matches a principal's
            actual role on each app against this list (case-insensitive substring).
            Empty array = "any role acceptable". Omit an app = "not expected to
            have access".
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-3 px-6 pb-2'>
          <div className='space-y-1'>
            <Label htmlFor='role-name'>Name *</Label>
            <Input
              id='role-name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Engineer'
              maxLength={120}
              required
              className='h-9'
            />
          </div>
          <div className='space-y-1'>
            <Label htmlFor='role-desc'>Description (optional)</Label>
            <Textarea
              id='role-desc'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={2000}
            />
          </div>
          <div className='space-y-1'>
            <Label htmlFor='role-expected'>Expected roles per app (JSON)</Label>
            <Textarea
              id='role-expected'
              value={expectedText}
              onChange={(e) => setExpectedText(e.target.value)}
              rows={6}
              className='font-mono text-[11px]'
              placeholder='{"azure": ["User", "Developer"], "github": ["write"]}'
            />
            <p className='text-[10px] text-text-subtle'>
              App slug → list of acceptable role-name substrings. The reconciler
              matches case-insensitively against the principal's raw role + group names.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={readOnly || pending || !name.trim()}>
            {pending ? 'Saving…' : existing ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
